import { notify } from '@billy/design-system'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { queryClient } from '../../../config/queryClient'
import { QueryKeys } from '../../../enums/queryKeys'
import { APIError } from '../../../utils'
import { InvoiceDocumentType } from '../enums/invoiceDocumentType'
import { InvoiceStatus } from '../enums/invoiceStatus'
import { getInvoiceQueryKey } from '../utils/getInvoiceQueryKey'
import { getManualDeliveryStatusNotification } from '../utils/getManualDeliveryStatusNotification'
import { useSetInvoiceStatus } from './useSetInvoiceStatus'

interface UseInvoiceStatusActionsParams {
  documentType: InvoiceDocumentType
  invoiceId: string
}

interface ManualDeliveryStatusActionOptions {
  onSuccess?: () => void
  onError?: (error: APIError) => void
}

export const useInvoiceStatusActions = ({ documentType, invoiceId }: UseInvoiceStatusActionsParams) => {
  const { t } = useTranslation()
  const { setInvoiceStatus, isProcessing } = useSetInvoiceStatus()

  const setManualDeliveryStatus = useCallback(
    (targetStatus: InvoiceStatus.Sent | InvoiceStatus.Unsent, options?: ManualDeliveryStatusActionOptions) => {
      const { id, successMessage, errorMessage } = getManualDeliveryStatusNotification(targetStatus, documentType, t)

      setInvoiceStatus(
        { documentType, invoiceId: Number(invoiceId), payload: { status: targetStatus } },
        {
          onSuccess: () => {
            notify({ id, message: successMessage, variant: 'success' })
            queryClient.invalidateQueries(getInvoiceQueryKey(invoiceId, documentType))
            queryClient.invalidateQueries(QueryKeys.InvoicesZervant)
            queryClient.invalidateQueries(QueryKeys.ClientInvoicesZervant)
            options?.onSuccess?.()
          },
          onError: (error) => {
            notify({ id, message: errorMessage, variant: 'error' })
            options?.onError?.(error)
          },
        },
      )
    },
    [documentType, invoiceId, setInvoiceStatus, t],
  )

  const markInvoiceAsSent = useCallback(
    (options?: ManualDeliveryStatusActionOptions) => setManualDeliveryStatus(InvoiceStatus.Sent, options),
    [setManualDeliveryStatus],
  )

  const unmarkInvoiceAsSent = useCallback(
    (options?: ManualDeliveryStatusActionOptions) => setManualDeliveryStatus(InvoiceStatus.Unsent, options),
    [setManualDeliveryStatus],
  )

  return { markInvoiceAsSent, unmarkInvoiceAsSent, isProcessing }
}
